import React, { useState, Suspense, lazy } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { ArrowLeft, LightningChargeFill, GraphUpArrow, SearchHeartFill } from 'react-bootstrap-icons';
import { DEFAULT_TEMPLATES } from '../../utils/defaultJournalTemplates';
import { useTheme } from '../../contexts/ThemeContext';

// Lazy to avoid circular import with JournalTemplateBuilder → JournalTemplateSelector
const JournalTemplateBuilder = lazy(() => import('./JournalTemplateBuilder'));

const TEMPLATE_ICONS = {
  lightning: LightningChargeFill,
  chart: GraphUpArrow,
  search: SearchHeartFill,
};

/**
 * @param {boolean} [editBeforeSave=false] - show "Preview & edit" on cards; that link opens the builder before save
 */
const JournalTemplateSelector = ({
  onSelectTemplate,
  currentTemplateName,
  showCard = true,
  buttonLabel = 'Select Template',
  editBeforeSave = false,
  buttonStyle = null,
}) => {
  const { colors } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState('list'); // list | edit
  const [editingTemplate, setEditingTemplate] = useState(null);

  const closeModal = () => {
    setShowModal(false);
    setStep('list');
    setEditingTemplate(null);
  };

  /** Select template as active journal — close modal, no builder */
  const handleSelectAsTemplate = (template) => {
    closeModal();
    if (typeof onSelectTemplate === 'function') {
      onSelectTemplate(template);
    }
  };

  /** Open builder to preview/edit prompts, then Save */
  const handlePreviewAndEdit = (template, e) => {
    e?.stopPropagation?.();
    setEditingTemplate(template);
    setStep('edit');
  };

  const handleBuilderSaved = (result) => {
    closeModal();
    if (typeof onSelectTemplate === 'function') {
      onSelectTemplate(result?.template || editingTemplate, { saved: true, name: result?.name });
    }
  };

  const outlineBtnStyle = {
    borderRadius: 50,
    fontSize: '0.85em',
    padding: '6px 16px',
    minWidth: 'auto',
    fontWeight: 600,
    background: 'transparent',
    color: colors.primary,
    border: `1.5px solid ${colors.primary}`,
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    ...buttonStyle,
  };

  const button = (
    <button
      type="button"
      onClick={() => {
        setStep('list');
        setEditingTemplate(null);
        setShowModal(true);
      }}
      style={outlineBtnStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `${colors.primary}14`;
        e.currentTarget.style.boxShadow = `0 2px 8px rgba(99, 102, 241, 0.18)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {buttonLabel}
    </button>
  );

  const templateModal = (
    <Modal
      show={showModal}
      onHide={closeModal}
      centered
      size={step === 'edit' ? 'lg' : undefined}
      scrollable={step === 'edit'}
    >
      <Modal.Header closeButton style={{ backgroundColor: colors.bgSecondary, borderColor: colors.border }}>
        <Modal.Title style={{ color: colors.text, fontSize: '1.1rem' }}>
          {step === 'edit'
            ? `Edit · ${editingTemplate?.name || 'Template'}`
            : 'Select Journal Template'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: colors.bgSecondary, maxHeight: step === 'edit' ? '70vh' : undefined }}>
        {step === 'list' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {DEFAULT_TEMPLATES.map((template) => {
              const Icon = TEMPLATE_ICONS[template.icon] || LightningChargeFill;
              const accent = template.accent || colors.primary;
              const tags = template.tags || [];
              const previewLabels = (template.fields || [])
                .filter((f) => !f.autoFill)
                .slice(0, 4)
                .map((f) => f.label);

              return (
                <div
                  key={template.name}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectAsTemplate(template);
                    }
                  }}
                  style={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: '10px',
                    padding: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: colors.bgCard,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = accent;
                    e.currentTarget.style.backgroundColor = `${accent}12`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.backgroundColor = colors.bgCard;
                  }}
                  onClick={() => handleSelectAsTemplate(template)}
                >
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: `${accent}18`,
                        color: accent,
                      }}
                    >
                      <Icon size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.5rem', marginBottom: '0.35rem' }}>
                        <h6 style={{ color: colors.text, margin: 0, fontWeight: 600 }}>{template.name}</h6>
                        <span
                          style={{
                            color: colors.textMuted,
                            fontSize: '0.75rem',
                            backgroundColor: colors.bgInput,
                            padding: '2px 8px',
                            borderRadius: '12px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {template.fields.length} fields
                        </span>
                      </div>
                      {tags.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.4rem' }}>
                          {tags.map((tag) => (
                            <span
                              key={tag}
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                letterSpacing: '0.02em',
                                color: accent,
                                backgroundColor: `${accent}14`,
                                border: `1px solid ${accent}33`,
                                padding: '2px 8px',
                                borderRadius: '999px',
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
                        {template.description}
                      </p>
                      {previewLabels.length > 0 && (
                        <div style={{ marginTop: '0.55rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {previewLabels.map((label) => (
                            <span
                              key={label}
                              style={{
                                fontSize: '0.75rem',
                                color: colors.textMuted,
                                backgroundColor: colors.bgInput,
                                padding: '2px 6px',
                                borderRadius: '4px',
                              }}
                            >
                              {label}
                            </span>
                          ))}
                          {(template.fields || []).filter((f) => !f.autoFill).length > previewLabels.length && (
                            <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                              +{(template.fields || []).filter((f) => !f.autoFill).length - previewLabels.length} more
                            </span>
                          )}
                        </div>
                      )}
                      {editBeforeSave && (
                        <button
                          type="button"
                          onClick={(e) => handlePreviewAndEdit(template, e)}
                          style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: accent,
                            fontWeight: 600,
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                            textDecoration: 'none',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = 'none';
                          }}
                        >
                          Preview &amp; edit all prompts →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: '0.25rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
              <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>
                {editBeforeSave
                  ? 'Click a card to use it, or preview & edit prompts before saving'
                  : 'You can edit any template after selecting it'}
              </p>
            </div>
          </div>
        )}

        {step === 'edit' && editingTemplate && (
          <div>
            <button
              type="button"
              onClick={() => {
                setStep('list');
                setEditingTemplate(null);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'none',
                border: 'none',
                color: colors.textMuted,
                fontSize: 13,
                padding: 0,
                marginBottom: 12,
                cursor: 'pointer',
              }}
            >
              <ArrowLeft size={14} /> Back to templates
            </button>
            <Suspense
              fallback={
                <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                  <Spinner animation="border" size="sm" />
                </div>
              }
            >
              <JournalTemplateBuilder
                key={editingTemplate.name}
                seedTemplate={editingTemplate}
                hideSelector
                autoSave={false}
                autoLoadSaved={false}
                showExplicitSave
                onSaveSuccess={handleBuilderSaved}
                onCancel={() => {
                  setStep('list');
                  setEditingTemplate(null);
                }}
              />
            </Suspense>
          </div>
        )}
      </Modal.Body>
      {step === 'list' && (
        <Modal.Footer style={{ backgroundColor: colors.bgSecondary, borderColor: colors.border }}>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );

  if (!showCard) {
    return (
      <>
        {button}
        {templateModal}
      </>
    );
  }

  return (
    <>
      <div
        style={{
          backgroundColor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <p style={{ color: colors.text, fontSize: '0.9rem', marginBottom: '0.25rem', fontWeight: 500 }}>
              Template: {currentTemplateName || 'Custom'}
            </p>
            <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
              Start with a default template or build your own
            </p>
          </div>
          {button}
        </div>
      </div>
      {templateModal}
    </>
  );
};

export default JournalTemplateSelector;
